import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : '';

function UniversalChat({ currentUserId, canWrite = true, allowGroupCreate = false }) {
  const { user } = useAuth();
  const myId = currentUserId || user?.userId;

  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [chatSearch, setChatSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [sending, setSending] = useState(false);
  const [activeGroupMembers, setActiveGroupMembers] = useState([]);
  const [showGroupInfo, setShowGroupInfo] = useState(false);

  const [showStartChat, setShowStartChat] = useState(false);
  const [startChatSearch, setStartChatSearch] = useState('');

  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupMembers, setGroupMembers] = useState([]);
  const [groupSearch, setGroupSearch] = useState('');
  const [addMemberSearch, setAddMemberSearch] = useState('');
  const [addingMemberId, setAddingMemberId] = useState(null);

  const msgEndRef = useRef(null);
  const pollMessagesRef = useRef(null);
  const pollChannelsRef = useRef(null);
  const fileRef = useRef(null);

  const loadChannels = useCallback(async () => {
    try {
      const data = await api.getChannels();
      const rows = Array.isArray(data) ? data : [];
      setChannels(rows);
      if (!activeChannel && rows.length > 0) setActiveChannel(rows[0].channel_id);
    } catch (e) { /* silent */ }
  }, [activeChannel]);

  const loadMessages = useCallback(async (channelId) => {
    if (!channelId) return;
    try {
      const data = await api.getMessages(channelId);
      setMessages(Array.isArray(data) ? data : []);
      setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
    } catch (e) { /* silent */ }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const data = await api.getChatUsers();
      const rows = Array.isArray(data) ? data : [];
      setAllUsers(rows.filter(u => u.id !== myId && (u.role || '').toLowerCase() !== 'superadmin'));
    } catch (e) { /* silent */ }
  }, [myId]);

  useEffect(() => {
    loadChannels();
    loadUsers();
  }, [loadChannels, loadUsers]);

  useEffect(() => {
    if (!activeChannel) return;
    loadMessages(activeChannel);
    if (pollMessagesRef.current) clearInterval(pollMessagesRef.current);
    pollMessagesRef.current = setInterval(() => loadMessages(activeChannel), 3000);
    return () => {
      if (pollMessagesRef.current) clearInterval(pollMessagesRef.current);
    };
  }, [activeChannel, loadMessages]);

  useEffect(() => {
    const activeMeta = channels.find(c => c.channel_id === activeChannel);
    if (!activeMeta || activeMeta.type !== 'group') {
      setActiveGroupMembers([]);
      setShowGroupInfo(false);
      return;
    }
    let stopped = false;
    const loadMembers = async () => {
      try {
        const rows = await api.getGroupMembers(activeChannel);
        if (!stopped) setActiveGroupMembers(Array.isArray(rows) ? rows : []);
      } catch {
        if (!stopped) setActiveGroupMembers([]);
      }
    };
    loadMembers();
    return () => { stopped = true; };
  }, [activeChannel, channels]);

  useEffect(() => {
    pollChannelsRef.current = setInterval(() => loadChannels(), 4000);
    return () => {
      if (pollChannelsRef.current) clearInterval(pollChannelsRef.current);
    };
  }, [loadChannels]);

  const filteredChannels = channels.filter(c => {
    if (filter === 'dm' && c.type !== 'dm') return false;
    if (filter === 'group' && c.type === 'dm') return false;
    if (!chatSearch.trim()) return true;
    return (c.name || '').toLowerCase().includes(chatSearch.toLowerCase());
  });

  const startChatUsers = allUsers.filter(u =>
    (u.name || '').toLowerCase().includes(startChatSearch.toLowerCase()) ||
    (u.emp_id || '').toLowerCase().includes(startChatSearch.toLowerCase()) ||
    (u.role || '').toLowerCase().includes(startChatSearch.toLowerCase())
  );

  const creatableGroupUsers = allUsers.filter(u =>
    (u.name || '').toLowerCase().includes(groupSearch.toLowerCase()) ||
    (u.emp_id || '').toLowerCase().includes(groupSearch.toLowerCase()) ||
    (u.role || '').toLowerCase().includes(groupSearch.toLowerCase())
  );

  const startDM = async (targetUserId) => {
    try {
      const res = await api.getOrCreateDM(targetUserId);
      if (res?.channelId) {
        setActiveChannel(res.channelId);
        setShowStartChat(false);
        setStartChatSearch('');
        await loadChannels();
      }
    } catch (e) { /* silent */ }
  };

  const toggleGroupMember = (uid) => {
    setGroupMembers(prev => prev.includes(uid) ? prev.filter(v => v !== uid) : [...prev, uid]);
  };

  const createGroup = async () => {
    if (!groupName.trim() || groupMembers.length === 0) return;
    try {
      const res = await api.createChannel(groupName.trim(), 'group', groupMembers);
      setShowCreateGroup(false);
      setGroupName('');
      setGroupMembers([]);
      setGroupSearch('');
      await loadChannels();
      if (res?.channelId) setActiveChannel(res.channelId);
    } catch (e) {
      alert(`Error: ${e.message}`);
    }
  };

  const sendMessage = async () => {
    if (!newMsg.trim() || !activeChannel || sending) return;
    setSending(true);
    try {
      await api.sendMessage(activeChannel, newMsg.trim());
      setNewMsg('');
      await loadMessages(activeChannel);
      await loadChannels();
    } catch (e) { /* silent */ }
    finally { setSending(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !activeChannel) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.uploadChatFile(formData);
      await api.sendMessage(activeChannel, file.name, res.fileUrl || res.url, file.name);
      await loadMessages(activeChannel);
      await loadChannels();
    } catch (err) {
      alert(`Upload failed: ${err.message}`);
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const fmtTime = (t) => {
    if (!t) return '';
    try { return new Date(t).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  };

  const fmtDateTime = (t) => {
    if (!t) return '-';
    try {
      return new Date(t).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const getInitials = (name) => (name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  const active = channels.find(c => c.channel_id === activeChannel);
  const isActiveGroup = active?.type === 'group';
  const amIGroupAdmin = activeGroupMembers.some(
    (m) => Number(m.user_id) === Number(myId) && String(m.role || '').toLowerCase() === 'admin'
  ) || String(active?.member_role || '').toLowerCase() === 'admin';
  const canManageGroupMembers = Boolean(canWrite && allowGroupCreate && isActiveGroup && amIGroupAdmin);
  const addableMembers = allUsers.filter((u) => {
    if (activeGroupMembers.some((m) => Number(m.user_id) === Number(u.id))) return false;
    const q = addMemberSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      String(u.name || '').toLowerCase().includes(q) ||
      String(u.emp_id || '').toLowerCase().includes(q) ||
      String(u.role || '').toLowerCase().includes(q)
    );
  });

  const addMemberToActiveGroup = async (userId) => {
    if (!activeChannel || !userId || addingMemberId) return;
    try {
      setAddingMemberId(userId);
      await api.addMemberToGroup(activeChannel, userId);
      setAddMemberSearch('');
      await loadChannels();
      await loadMessages(activeChannel);
      const rows = await api.getGroupMembers(activeChannel);
      setActiveGroupMembers(Array.isArray(rows) ? rows : []);
    } catch (err) {
      alert(`Could not add member: ${err.message}`);
    } finally {
      setAddingMemberId(null);
    }
  };

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 180px)', background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
      <div style={{ width: 300, borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: 12, borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <button className={`tab-btn${filter === 'all' ? ' active' : ''}`} onClick={() => setFilter('all')}>All</button>
            <button className={`tab-btn${filter === 'dm' ? ' active' : ''}`} onClick={() => setFilter('dm')}>Individual</button>
            <button className={`tab-btn${filter === 'group' ? ' active' : ''}`} onClick={() => setFilter('group')}>Group</button>
          </div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            {canWrite && <button className="btn-outline" style={{ fontSize: 11, padding: '4px 8px' }} onClick={() => { setShowStartChat(!showStartChat); setShowCreateGroup(false); }}>Start Chat</button>}
            {canWrite && allowGroupCreate && <button className="btn-primary" style={{ fontSize: 11, padding: '4px 8px' }} onClick={() => { setShowCreateGroup(!showCreateGroup); setShowStartChat(false); }}>Create Group</button>}
          </div>
          <input
            value={chatSearch}
            onChange={e => setChatSearch(e.target.value)}
            placeholder="Search chats..."
            style={{ width: '100%', padding: '7px 12px', borderRadius: 20, border: '1px solid #e2e8f0', fontSize: 12, boxSizing: 'border-box', outline: 'none' }}
          />
        </div>

        {canWrite && showStartChat && (
          <div style={{ padding: '8px 12px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
            <input
              value={startChatSearch}
              onChange={e => setStartChatSearch(e.target.value)}
              placeholder="Search user..."
              style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, marginBottom: 6, boxSizing: 'border-box' }}
            />
            <div style={{ maxHeight: 190, overflow: 'auto' }}>
              {startChatUsers.map(u => (
                <div key={u.id} onClick={() => startDM(u.id)} style={{ padding: '8px 6px', cursor: 'pointer', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: '#6366f1', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 11 }}>
                    {getInitials(u.name)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{u.name}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>{u.emp_id} · {u.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {canWrite && allowGroupCreate && showCreateGroup && (
          <div style={{ padding: '8px 12px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
            <input
              value={groupName}
              onChange={e => setGroupName(e.target.value)}
              placeholder="Group name"
              style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, marginBottom: 6, boxSizing: 'border-box' }}
            />
            <input
              value={groupSearch}
              onChange={e => setGroupSearch(e.target.value)}
              placeholder="Search members..."
              style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, marginBottom: 6, boxSizing: 'border-box' }}
            />
            <div style={{ maxHeight: 160, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, padding: 4, background: '#fff' }}>
              {creatableGroupUsers.map(u => (
                <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 6, fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={groupMembers.includes(u.id)} onChange={() => toggleGroupMember(u.id)} />
                  <span>{u.name}</span>
                  <span style={{ color: '#94a3b8', fontSize: 10 }}>{u.role}</span>
                </label>
              ))}
            </div>
            <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn-submit" style={{ fontSize: 11, padding: '5px 10px' }} onClick={createGroup} disabled={!groupName.trim() || groupMembers.length === 0}>Create</button>
            </div>
          </div>
        )}

        <div style={{ flex: 1, overflow: 'auto' }}>
          {filteredChannels.map(c => (
            <div key={c.channel_id} onClick={() => setActiveChannel(c.channel_id)}
              style={{
                padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f8fafc',
                background: activeChannel === c.channel_id ? '#eef2ff' : 'transparent',
                display: 'flex', alignItems: 'center', gap: 10
              }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: c.type === 'dm' ? '#8b5cf6' : '#2563eb', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 11 }}>
                {getInitials(c.name || c.otherUser?.name)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.name || c.otherUser?.name || 'Chat'}
                </div>
                <div style={{ color: '#94a3b8', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.last_message || 'Start to chat'}
                </div>
              </div>
            </div>
          ))}
          {filteredChannels.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>
              No conversations yet
            </div>
          )}
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {active && activeChannel ? (
          <>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', fontWeight: 700, fontSize: 14, background: '#fafbfc' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <div>{active.name || active.otherUser?.name || 'Chat'}</div>
                {isActiveGroup && (
                  <button
                    type="button"
                    onClick={() => setShowGroupInfo(v => !v)}
                    style={{
                      border: '1px solid #cbd5e1',
                      background: '#fff',
                      borderRadius: 8,
                      fontSize: 11,
                      padding: '5px 8px',
                      cursor: 'pointer',
                      color: '#334155',
                      fontWeight: 600
                    }}
                  >
                    {(active.name || 'Group')} {showGroupInfo ? '▲' : '▼'}
                  </button>
                )}
              </div>
              {isActiveGroup && showGroupInfo && (
                <div style={{
                  marginTop: 8,
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: 10,
                  padding: 10
                }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
                    {(active.name || 'Group')}
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>
                    Created by: {(activeGroupMembers.find(m => String(m.role || '').toLowerCase() === 'admin') || activeGroupMembers[0])?.name || 'Unknown'} | Created at: {fmtDateTime(active.created_at || (activeGroupMembers.find(m => String(m.role || '').toLowerCase() === 'admin') || activeGroupMembers[0])?.joined_at)}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#334155', marginBottom: 6 }}>
                    Members ({activeGroupMembers.length})
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 6 }}>
                    {activeGroupMembers.map((m) => (
                      <div key={m.user_id} style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: 8,
                        padding: '6px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        background: '#f8fafc'
                      }}>
                        <div style={{
                          width: 26,
                          height: 26,
                          borderRadius: 7,
                          background: String(m.role || '').toLowerCase() === 'admin' ? '#4338ca' : '#0ea5e9',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 10,
                          fontWeight: 700
                        }}>
                          {getInitials(m.name)}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {m.name}
                          </div>
                          <div style={{ fontSize: 10, color: '#64748b' }}>
                            {String(m.role || '').toLowerCase() === 'admin' ? 'Admin' : 'Member'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {canManageGroupMembers && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #e2e8f0' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#334155', marginBottom: 6 }}>
                        Add Members
                      </div>
                      <input
                        value={addMemberSearch}
                        onChange={(e) => setAddMemberSearch(e.target.value)}
                        placeholder="Search users to add..."
                        style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, marginBottom: 6, boxSizing: 'border-box' }}
                      />
                      <div style={{ maxHeight: 120, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                        {addableMembers.length === 0 ? (
                          <div style={{ padding: 8, fontSize: 11, color: '#94a3b8' }}>No users available</div>
                        ) : addableMembers.slice(0, 25).map((u) => (
                          <div key={u.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: 8, borderBottom: '1px solid #f1f5f9' }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.name}</div>
                              <div style={{ fontSize: 10, color: '#64748b' }}>{u.emp_id} | {u.role}</div>
                            </div>
                            <button
                              type="button"
                              onClick={() => addMemberToActiveGroup(u.id)}
                              disabled={addingMemberId === u.id}
                              style={{
                                border: '1px solid #cbd5e1',
                                background: '#fff',
                                borderRadius: 6,
                                fontSize: 10,
                                fontWeight: 700,
                                color: '#334155',
                                padding: '4px 8px',
                                cursor: addingMemberId === u.id ? 'not-allowed' : 'pointer'
                              }}
                            >
                              {addingMemberId === u.id ? 'Adding...' : 'Add'}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px', background: '#f8fafc' }}>
              {messages.length === 0 && (
                <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 13, marginTop: 40 }}>
                  Start to chat
                </div>
              )}
              {messages.map((m, i) => {
                const isMe = m.sender_id === myId;
                const isSystem = m.type === 'system';
                const isFile = m.type === 'file';
                const fileHref = String(m.file_url || '').startsWith('http') ? m.file_url : `${API_BASE}${m.file_url || ''}`;
                return (
                  <div key={m.id || i} style={{ marginBottom: 14, display: 'flex', justifyContent: isSystem ? 'center' : isMe ? 'flex-end' : 'flex-start' }}>
                    {isSystem ? (
                      <div style={{ background: '#f1f5f9', padding: '4px 12px', borderRadius: 16, fontSize: 11, color: '#64748b' }}>{m.message}</div>
                    ) : (
                      <div style={{ maxWidth: '65%', minWidth: 100 }}>
                        {!isMe && <div style={{ fontSize: 10, fontWeight: 600, color: '#6366f1', marginBottom: 3, marginLeft: 4 }}>{m.sender_name || 'User'}</div>}
                        <div style={{
                          padding: '10px 14px', borderRadius: 14,
                          background: isMe ? 'linear-gradient(135deg,#4338ca,#6366f1)' : '#fff',
                          color: isMe ? '#fff' : '#0f172a', fontSize: 13, lineHeight: 1.5,
                          wordBreak: 'break-word', border: isMe ? 'none' : '1px solid #e2e8f0'
                        }}>
                          {isFile && m.file_url ? (
                            <span style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                              {/* File name — NOT a link to prevent new tab / S3 AccessDenied */}
                              <span style={{ color: isMe ? '#e0e7ff' : '#374151', fontSize: 12 }}>
                                📎 {m.file_name || m.message || 'File'}
                              </span>
                              <button
                                type="button"
                                onClick={() => api.downloadChatFile(m.file_url, m.file_name || 'file')}
                                style={{
                                  border: 'none',
                                  background: isMe ? 'rgba(255,255,255,.2)' : '#eff6ff',
                                  color: isMe ? '#fff' : '#2563eb',
                                  fontSize: 11,
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                  padding: '3px 10px',
                                  borderRadius: 6,
                                }}
                              >
                                ⬇ Download
                              </button>
                            </span>
                          ) : m.message}
                        </div>
                        <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 3, textAlign: isMe ? 'right' : 'left' }}>
                          {fmtTime(m.sent_at || m.created_at)}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              <div ref={msgEndRef} />
            </div>

            {canWrite ? (
              <div style={{ padding: '10px 16px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: 8, alignItems: 'center', background: '#fff' }}>
                <label style={{ cursor: 'pointer', padding: '8px 10px', background: '#f1f5f9', borderRadius: 8, fontSize: 16 }}>
                  Upload
                  <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={handleFileUpload} />
                </label>
                <input
                  value={newMsg}
                  onChange={e => setNewMsg(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                  placeholder="Type a message..."
                  style={{ flex: 1, padding: '9px 14px', borderRadius: 24, border: '1.5px solid #e2e8f0', fontSize: 13, outline: 'none' }}
                />
                <button onClick={sendMessage} disabled={!newMsg.trim() || sending}
                  style={{
                    background: !newMsg.trim() || sending ? '#e2e8f0' : 'linear-gradient(135deg,#4338ca,#6366f1)',
                    color: !newMsg.trim() || sending ? '#94a3b8' : '#fff', border: 'none', borderRadius: 24, padding: '9px 16px', cursor: !newMsg.trim() || sending ? 'not-allowed' : 'pointer'
                  }}>
                  Send
                </button>
              </div>
            ) : (
              <div style={{ padding: '10px 16px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', fontSize: 12, color: '#64748b', textAlign: 'center' }}>
                Read-only access
              </div>
            )}
          </>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#64748b' }}>Select a chat</div>
            <div style={{ fontSize: 12 }}>Start to chat</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UniversalChat;
